import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useNavigate, useParams} from 'react-router-dom';
import {
	ArrowLeft,
	ChartBar,
	ChatCircle,
	CheckCircle,
	CursorClick,
	Envelope,
	EnvelopeOpen,
	Info,
	PaperPlaneTilt,
	Warning,
} from '@phosphor-icons/react';
import {auth} from '../../firebase';
import {SigninForm} from '../molecules/SigninForm';

import 'leaflet/dist/leaflet.css';
import {CircleMarker, MapContainer, Popup, TileLayer} from 'react-leaflet';
import L from 'leaflet';

const NEUGENCE_API_BASE = 'https://app.neugence.com';

async function bearerHeaders() {
	const user = auth && auth.currentUser;
	if (!user) throw new Error('Not signed in');
	const token = await user.getIdToken();
	return {Authorization: `Bearer ${token}`};
}

function fmtNumber(n) {
	if (n == null || Number.isNaN(n)) return '—';
	if (typeof n !== 'number') return String(n);
	return n.toLocaleString();
}

function fmtPct(num, den) {
	if (!den || den === 0) return '—';
	return `${((num / den) * 100).toFixed(1)}%`;
}

function fmtTimestamp(tsMs) {
	if (!tsMs) return '—';
	try {
		return new Date(Number(tsMs)).toLocaleString();
	} catch (_e) {
		return '—';
	}
}

const TYPE_TO_LABEL = {
	send: 'Sent',
	delivery: 'Delivered',
	open: 'Opened (indicative)',
	click: 'Clicked',
	reply: 'Replied',
	conversion: 'Converted',
	bounce: 'Bounced',
	complaint: 'Complaint',
	reject: 'Rejected by SES',
	renderingFailure: 'Render failed',
};

const TYPE_TO_COLOR = {
	send: '#3b82f6',
	delivery: '#10b981',
	open: '#f59e0b',
	click: '#ef4444',
	reply: '#0ea5e9',
	conversion: '#059669',
	bounce: '#dc2626',
	complaint: '#7c3aed',
};

/* ------------------------------------------------------------------ */
/* Funnel — Sent → Delivered → Opened → Clicked                        */
/* ------------------------------------------------------------------ */
const FunnelStep = ({icon: Icon, label, count, prevCount, accent}) => {
	const pct = fmtPct(count, prevCount);
	return (
		<div className="flex-1 min-w-[140px] rounded-2xl border border-slate-200 bg-white p-5">
			<div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
				<Icon size={16} weight="fill" style={{color: accent}}/>
				<span>{label}</span>
			</div>
			<div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
				{fmtNumber(count)}
			</div>
			{prevCount != null && prevCount !== count && (
				<div className="mt-1 text-xs text-slate-500">
					{pct} of previous step
				</div>
			)}
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* World map — react-leaflet, geo_clusters from /events response       */
/* ------------------------------------------------------------------ */
const WorldMap = ({clusters, selectedType, onSelectType, allTypes}) => {
	const visibleClusters = useMemo(
		() => (clusters || []).filter((c) => c.type === selectedType),
		[clusters, selectedType],
	);

	if (!clusters || clusters.length === 0) {
		return (
			<div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
				No geo-tagged events yet. Map appears as opens / clicks roll in.
			</div>
		);
	}

	return (
		<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
			<div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
				<span className="text-sm font-semibold text-slate-900">Where events fired</span>
				<div className="ml-auto inline-flex rounded-full bg-slate-100 p-1">
					{allTypes.map((t) => {
						const active = t === selectedType;
						return (
							<button
								key={t}
								type="button"
								onClick={() => onSelectType(t)}
								className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
									active
										? 'bg-white text-slate-900 shadow-sm'
										: 'text-slate-600 hover:text-slate-900'
								}`}>
								{TYPE_TO_LABEL[t] || t}
							</button>
						);
					})}
				</div>
			</div>
			<div style={{height: 420}}>
				<MapContainer
					center={[20, 0]}
					zoom={2}
					minZoom={2}
					style={{height: '100%', width: '100%'}}
					scrollWheelZoom={false}
					worldCopyJump={true}>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{visibleClusters.map((c, i) => {
						if (c.lat == null || c.lng == null) return null;
						const radius = Math.min(20, 4 + Math.log2((c.count || 1) + 1) * 2);
						const color = TYPE_TO_COLOR[c.type] || '#6366f1';
						const isOpen = c.type === 'open';
						return (
							<CircleMarker
								key={i}
								center={[c.lat, c.lng]}
								radius={radius}
								pathOptions={{
									color,
									fillColor: color,
									fillOpacity: isOpen ? 0.35 : 0.7,
									weight: isOpen ? 1 : 2,
								}}>
								<Popup>
									<div className="text-xs">
										<div className="font-semibold">{TYPE_TO_LABEL[c.type] || c.type}</div>
										<div>{c.city || c.country || '(unknown)'}</div>
										<div>{c.count} event{c.count === 1 ? '' : 's'}</div>
									</div>
								</Popup>
							</CircleMarker>
						);
					})}
				</MapContainer>
			</div>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* Recipient table — events by recipient (sortable)                    */
/* ------------------------------------------------------------------ */
const RecipientTable = ({events}) => {
	const rows = useMemo(() => {
		const byRcpt = new Map();
		(events || []).forEach((e) => {
			const r = e.recipient || '(no recipient)';
			const cur = byRcpt.get(r) || {recipient: r, send: 0, delivery: 0, open: 0, click: 0, bounce: 0, lastTs: 0};
			cur[e.type] = (cur[e.type] || 0) + 1;
			const ts = Number(e.received_at_ms || 0);
			if (ts > cur.lastTs) cur.lastTs = ts;
			byRcpt.set(r, cur);
		});
		return Array.from(byRcpt.values()).sort((a, b) => b.lastTs - a.lastTs);
	}, [events]);

	if (rows.length === 0) {
		return null;
	}

	return (
		<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
			<div className="px-5 py-3 border-b border-slate-100">
				<span className="text-sm font-semibold text-slate-900">Per-recipient activity</span>
				<span className="ml-2 text-xs text-slate-500">{rows.length} unique recipients</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead className="text-xs text-slate-500 uppercase tracking-wider">
						<tr>
							<th className="text-left px-5 py-2">Recipient</th>
							<th className="text-right px-3 py-2">Sent</th>
							<th className="text-right px-3 py-2">Delivered</th>
							<th className="text-right px-3 py-2">Opens</th>
							<th className="text-right px-3 py-2">Clicks</th>
							<th className="text-right px-3 py-2">Bounces</th>
							<th className="text-left px-5 py-2">Last event</th>
						</tr>
					</thead>
					<tbody>
						{rows.slice(0, 100).map((r) => (
							<tr key={r.recipient} className="border-t border-slate-100">
								<td className="px-5 py-2 text-slate-900">{r.recipient}</td>
								<td className="px-3 py-2 text-right text-slate-700">{r.send || 0}</td>
								<td className="px-3 py-2 text-right text-slate-700">{r.delivery || 0}</td>
								<td className="px-3 py-2 text-right text-slate-700">{r.open || 0}</td>
								<td className="px-3 py-2 text-right text-slate-700">{r.click || 0}</td>
								<td className="px-3 py-2 text-right text-slate-700">{r.bounce || 0}</td>
								<td className="px-5 py-2 text-slate-500 text-xs">{fmtTimestamp(r.lastTs)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */
export default function CampaignDetail() {
	const {cid} = useParams();
	const navigate = useNavigate();
	const [campaign, setCampaign] = useState(null);
	const [eventsData, setEventsData] = useState(null);
	const [posthogSessions, setPosthogSessions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedMapType, setSelectedMapType] = useState('open');
	const [refreshTick, setRefreshTick] = useState(0);

	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const headers = await bearerHeaders();
			const [c, e] = await Promise.all([
				axios.get(`${NEUGENCE_API_BASE}/api/v1/campaigns/${cid}`, {headers}),
				axios.get(`${NEUGENCE_API_BASE}/api/v1/campaigns/${cid}/events?per_page=500`, {headers}),
			]);
			setCampaign(c.data || null);
			setEventsData(e.data || null);

			// PostHog session data — keyed by the campaign's utm_campaign tag.
			// Pulled lazily so an inaccessible PostHog never breaks the page.
			try {
				const utm = (c.data && (c.data.utm_campaign || c.data.name)) || '';
				if (utm) {
					const ph = await axios.get(`${NEUGENCE_API_BASE}/api/v1/posthog/sessions?period_h=720&utm_campaign=${encodeURIComponent(utm)}`, {headers});
					setPosthogSessions((ph.data && ph.data.rows) || []);
				}
			} catch (_phErr) {
				// silent — PostHog failures should not blank the page
				setPosthogSessions([]);
			}
		} catch (err) {
			console.error('campaign detail load error', err);
			setError(err && err.message ? err.message : 'Could not load campaign');
		} finally {
			setLoading(false);
		}
	}, [cid]);

	useEffect(() => {
		if (!signedIn || !cid) return;
		load();
	}, [signedIn, cid, load, refreshTick]);

	// Auto-refresh every 30s when campaign is in-flight (Status: Running).
	useEffect(() => {
		if (!campaign || campaign.status !== 'Running') return;
		const id = setInterval(() => setRefreshTick((x) => x + 1), 30000);
		return () => clearInterval(id);
	}, [campaign]);

	if (!signedIn) {
		return (
			<div style={{marginTop: '300px'}}>
				<SigninForm/>
			</div>
		);
	}

	const byType = (eventsData && eventsData.by_type) || {};
	const sent = (byType.send && byType.send.unique) || 0;
	const delivered = (byType.delivery && byType.delivery.unique) || 0;
	const opens = (byType.open && byType.open.count) || 0;
	const opensUnique = (byType.open && byType.open.unique) || 0;
	const clicks = (byType.click && byType.click.count) || 0;
	const clicksUnique = (byType.click && byType.click.unique) || 0;
	const replies = (byType.reply && byType.reply.count) || (eventsData && eventsData.replies_total) || 0;
	const conversions = (byType.conversion && byType.conversion.count) || (eventsData && eventsData.conversions_total) || 0;
	const bounces = (byType.bounce && byType.bounce.unique) || 0;

	const recipientCount = Array.isArray(campaign && campaign.recipient_list)
		? campaign.recipient_list.length
		: 0;
	const sentDisplay = sent || (campaign && campaign.status === 'Done' ? recipientCount : 0);

	const mapTypes = ['open', 'click'].filter((t) => (byType[t] && byType[t].count) > 0);
	if (mapTypes.length === 0) mapTypes.push('open');
	const effectiveMapType = mapTypes.includes(selectedMapType) ? selectedMapType : mapTypes[0];

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<button
					type="button"
					onClick={() => navigate('/dashboard')}
					className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/>
					Dashboard
				</button>

				{/* Header */}
				<div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900">
							{(campaign && (campaign.name || campaign.subject)) || 'Campaign'}
						</h1>
						<p className="text-sm text-slate-500 mt-1">
							{(campaign && campaign.subject) || ''}
						</p>
						<div className="text-xs text-slate-400 mt-1">
							{recipientCount > 0 && <span>{recipientCount} recipients · </span>}
							{campaign && campaign.created_at && (
								<span>created {fmtTimestamp(campaign.created_at)}</span>
							)}
						</div>
					</div>
					<button
						type="button"
						onClick={() => setRefreshTick((x) => x + 1)}
						className="self-start rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
						Refresh
					</button>
				</div>

				{/* Privacy banner */}
				<div className="flex gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-6">
					<Info size={18} className="text-amber-600 flex-shrink-0 mt-0.5"/>
					<div className="text-xs text-amber-900 leading-relaxed">
						<strong>Open rate is indicative, not authoritative.</strong> Apple Mail Privacy
						Protection auto-loads images on delivery (false positives), Outlook blocks
						images by default (false negatives), and Gmail proxies through Google datacenters
						(geo will look US-clustered). Click-through and reply rate are the real signals.
					</div>
				</div>

				{/* Error / loading */}
				{error && !loading && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6">
						<div className="flex items-start gap-3">
							<Warning size={20} className="text-amber-600 mt-0.5"/>
							<div>
								<h3 className="text-sm font-semibold text-amber-900">Couldn't load campaign</h3>
								<p className="text-xs text-amber-800 mt-1">{String(error)}</p>
							</div>
						</div>
					</div>
				)}

				{loading && !eventsData && (
					<div className="text-center text-sm text-slate-500 py-20">Loading…</div>
				)}

				{!loading && eventsData && (
					<>
						{/* Funnel */}
						<div className="flex flex-wrap gap-3 mb-8">
							<FunnelStep icon={PaperPlaneTilt} label="Sent" count={sentDisplay} prevCount={null} accent={TYPE_TO_COLOR.send}/>
							<FunnelStep icon={Envelope} label="Delivered" count={delivered || sentDisplay} prevCount={sentDisplay} accent={TYPE_TO_COLOR.delivery}/>
							<FunnelStep icon={EnvelopeOpen} label="Opens" count={opens} prevCount={delivered || sentDisplay} accent={TYPE_TO_COLOR.open}/>
							<FunnelStep icon={CursorClick} label="Clicks" count={clicks} prevCount={opens || delivered || sentDisplay} accent={TYPE_TO_COLOR.click}/>
							<FunnelStep icon={ChatCircle} label="Replied" count={replies} prevCount={clicks || opens || delivered || sentDisplay} accent={TYPE_TO_COLOR.reply}/>
							<FunnelStep icon={CheckCircle} label="Converted" count={conversions} prevCount={clicks || opens || delivered || sentDisplay} accent={TYPE_TO_COLOR.conversion}/>
							{bounces > 0 && (
								<FunnelStep icon={Warning} label="Bounces" count={bounces} prevCount={sentDisplay} accent={TYPE_TO_COLOR.bounce}/>
							)}
						</div>

						{/* Unique-vs-total chip row */}
						<div className="flex flex-wrap gap-2 mb-8 text-xs text-slate-600">
							{opens > 0 && (
								<span className="rounded-full bg-slate-100 px-3 py-1">
									Opens: <strong>{fmtNumber(opensUnique)}</strong> unique recipients,{' '}
									<strong>{fmtNumber(opens)}</strong> total events
								</span>
							)}
							{clicks > 0 && (
								<span className="rounded-full bg-slate-100 px-3 py-1">
									Clicks: <strong>{fmtNumber(clicksUnique)}</strong> unique,{' '}
									<strong>{fmtNumber(clicks)}</strong> total
								</span>
							)}
						</div>

						{/* World map */}
						<div className="mb-8">
							<WorldMap
								clusters={eventsData.geo_clusters}
								selectedType={effectiveMapType}
								onSelectType={setSelectedMapType}
								allTypes={mapTypes}
							/>
						</div>

						{/* Recipient table */}
						<RecipientTable events={eventsData.events}/>

						{/* PostHog sessions — recipients who actually visited whipscribe.com */}
						{posthogSessions.length > 0 && (
							<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mt-8">
								<div className="px-5 py-3 border-b border-slate-100">
									<span className="text-sm font-semibold text-slate-900">PostHog sessions on whipscribe.com</span>
									<span className="ml-2 text-xs text-slate-500">{posthogSessions.length} sessions for this campaign · joined by utm_campaign</span>
								</div>
								<table className="w-full text-sm">
									<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
										<tr>
											<th className="text-right px-3 py-2">Duration</th>
											<th className="text-right px-3 py-2">Pageviews</th>
											<th className="text-left px-3 py-2">Country</th>
											<th className="text-left px-3 py-2">Distinct ID</th>
											<th className="text-left px-3 py-2">Started</th>
										</tr>
									</thead>
									<tbody>
										{posthogSessions.map((s,i) => (
											<tr key={s.session_id || i} className="border-t border-slate-100">
												<td className="px-3 py-2 text-right font-medium text-slate-900">{s.duration_s ? `${s.duration_s.toFixed(0)}s` : '0s'}</td>
												<td className="px-3 py-2 text-right text-slate-700">{s.pageviews}</td>
												<td className="px-3 py-2 text-slate-700 text-xs">{s.country || '—'} {s.city ? `· ${s.city}` : ''}</td>
												<td className="px-3 py-2 text-slate-500 text-xs font-mono">{(s.distinct_id||'').substring(0,14)}…</td>
												<td className="px-3 py-2 text-slate-500 text-xs">{fmtTimestamp(new Date(s.started_at).getTime())}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
